/**
 * PurchaseFlow for the web (contracts/billing.ts, web.md §2): the mock provider now — offers →
 * checkout → mock confirm. Paddle's overlay replaces `purchase` later; nothing else changes.
 *
 * Every outcome that changes the plan is handed to `onEntitlement` (the session applies it to the
 * engine: limits, sync start/stop), so the subscription screen only renders results.
 */

import {
  DigesterError, type ApiCaller, type BillingCheckoutRequest, type BillingCheckoutResponse, type BillingEntitlementResponse,
  type BillingOffersResponse, type BillingProviderId, type Entitlement, type PlanOffer, type PurchaseFlow, type PurchaseOutcome,
} from '@digester/core';

export interface WebPurchaseFlowOptions {
  api: ApiCaller;
  /** Where a real provider returns the browser to (unused by the mock). */
  returnUrl: string;
  onEntitlement(entitlement: Entitlement): Promise<void>;
}

export function createWebPurchaseFlow(options: WebPurchaseFlowOptions): PurchaseFlow {
  const { api } = options;
  let provider: BillingProviderId = 'mock';

  const entitlementOf = (res: BillingEntitlementResponse): Entitlement => {
    if (!res || typeof res.entitlement !== 'object' || res.entitlement === null) {
      throw new DigesterError('internal', 'The server sent an unexpected billing response');
    }
    return res.entitlement;
  };

  return {
    get provider() {
      return provider;
    },
    async offers(): Promise<PlanOffer[]> {
      const res = await api.call<BillingOffersResponse>('billingOffers');
      provider = res.provider;
      return res.offers;
    },
    async purchase(offer): Promise<PurchaseOutcome> {
      const body: BillingCheckoutRequest = { multiplier: offer.multiplier, period: offer.period, returnUrl: options.returnUrl };
      const session = await api.call<BillingCheckoutResponse>('billingCheckout', { body });
      provider = session.provider;
      if (session.provider !== 'mock') {
        // Paddle / RevenueCat flows are not part of this build (architecture.md "Seams").
        throw new DigesterError('unsupported', `Purchases through ${session.provider} are not available in this build.`);
      }
      const confirmed = entitlementOf(await api.call<BillingEntitlementResponse>('billingMockConfirm', { body: { reference: session.reference } }));
      await options.onEntitlement(confirmed);
      return { status: 'purchased', entitlement: confirmed };
    },
    async cancel(): Promise<Entitlement> {
      const entitlement = entitlementOf(await api.call<BillingEntitlementResponse>('billingCancel', { body: {} }));
      await options.onEntitlement(entitlement);
      return entitlement;
    },
    async restore(): Promise<Entitlement> {
      const entitlement = entitlementOf(await api.call<BillingEntitlementResponse>('billingEntitlement'));
      await options.onEntitlement(entitlement);
      return entitlement;
    },
  };
}
